import { useEffect, useMemo, useState } from "react";
import "../../../V2SideDrawer.css";
import "../styles/UserInviteGuestDrawer.css";

export default function UserInviteGuestDrawer({
  open,
  onClose,
  onInvite,
  onRefreshGroups,
  inviting = false,
  groups = [],
  groupsLoading = false,
  initialEmail = "",
  initialDisplayName = "",
  lockIdentity = false,
}) {
  const [form, setForm] = useState({
    email: "",
    displayName: "",
    groupId: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setForm({
      email: initialEmail || "",
      displayName: initialDisplayName || "",
      groupId: "",
    });
    setErrors({});
  }, [open, initialEmail, initialDisplayName]);

  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => {
      if (e.key === "Escape" && !inviting) onClose?.();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [open, inviting, onClose]);

  const groupOptions = useMemo(
    () => (Array.isArray(groups) ? groups : []).map((g) => ({ id: g.id, label: g.displayName || g.id })),
    [groups],
  );

  if (!open) return null;

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const validate = () => {
    const next = {};
    if (!form.email.trim()) next.email = "Required";
    else if (!form.email.includes("@")) next.email = "Enter a valid email";
    if (!form.displayName.trim()) next.displayName = "Required";
    if (!form.groupId) next.groupId = "Required";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleInvite = () => {
    if (!validate()) return;
    const selected = groupOptions.find((g) => g.id === form.groupId);
    onInvite?.({
      email: form.email.trim(),
      displayName: form.displayName.trim(),
      groupId: form.groupId,
      groupName: selected?.label || "",
    });
  };

  return (
    <>
      <div className="v2drw-scrim" onClick={() => !inviting && onClose?.()} />
      <div className="v2drw useri-drawer" role="dialog" aria-modal="true">
        <div className="v2drw-head">
          <div className="v2drw-head-main">
            <div className="v2drw-eyebrow">Users</div>
            <div className="v2drw-title">Invite External User</div>
          </div>
          <button className="v2drw-close" onClick={() => !inviting && onClose?.()} aria-label="Close">✕</button>
        </div>

        <div className="v2drw-body">
          <div className="userc-grid">
            <div className="userc-field">
              <label className="v2drw-form-label">Email *</label>
              <input
                className={`v2drw-control${errors.email ? " userc-err" : ""}`}
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                placeholder="customer.user@company.com"
                autoFocus
                readOnly={lockIdentity}
              />
              {errors.email && <span className="userc-err-text">{errors.email}</span>}
            </div>

            <div className="userc-field">
              <label className="v2drw-form-label">Display Name *</label>
              <input
                className={`v2drw-control${errors.displayName ? " userc-err" : ""}`}
                value={form.displayName}
                onChange={(e) => setField("displayName", e.target.value)}
                placeholder="Customer User Name"
                readOnly={lockIdentity}
              />
              {errors.displayName && <span className="userc-err-text">{errors.displayName}</span>}
            </div>

            <div className="userc-field useri-span-2">
              <div className="useri-group-label-row">
                <label className="v2drw-form-label">Customer Group *</label>
                {onRefreshGroups && (
                  <button
                    type="button"
                    className="useri-refresh-btn"
                    onClick={onRefreshGroups}
                    disabled={groupsLoading || inviting}
                    title="Refresh groups"
                  >
                    {groupsLoading ? "↻" : "↻ Refresh"}
                  </button>
                )}
              </div>
              <select
                className={`v2drw-control${errors.groupId ? " userc-err" : ""}`}
                value={form.groupId}
                onChange={(e) => setField("groupId", e.target.value)}
                disabled={groupsLoading}
              >
                <option value="">{groupsLoading ? "Loading groups..." : "Select customer group"}</option>
                {groupOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.label}</option>
                ))}
              </select>
              {errors.groupId && <span className="userc-err-text">{errors.groupId}</span>}
            </div>
          </div>
        </div>

        <div className="v2drw-foot">
          <span className="v2drw-foot-spacer" />
          <button className="v2drw-btn-cancel" onClick={onClose} disabled={inviting}>Cancel</button>
          <button className="v2drw-btn-save" onClick={handleInvite} disabled={inviting || groupsLoading}>
            {inviting ? "Inviting..." : "Invite User"}
          </button>
        </div>
      </div>
    </>
  );
}
